import React from 'react';
import { Link } from 'react-router-dom';

import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  TOpsClassDetailEx,
  TOpsClassRelatedEx,
} from '../../../requests/opsRegistrationsExtra';
import { btnGhost, Card } from '../ui';
import { TInstructorEval } from './shared';

// Read-only summaries of the records hanging off a class.

type Props = {
  id: string;
  related: TOpsClassRelatedEx;
  existing?: TOpsClassDetailEx;
};

export const RegistrationsCard: React.FC<Props> = ({
  id,
  related,
  existing,
}) => (
  <Card
    title={`Registrations · ${related.registrationCount}${
      existing?.seats != null ? ` of ${existing.seats} seats` : ''
    }`}
  >
    <div className="p-5 text-sm">
      <Link className={btnGhost} to={`${PATH.opsRegistrations}?classId=${id}`}>
        Open the roster →
      </Link>
    </div>
  </Card>
);

export const EvalResponsesCard: React.FC<{ related: TOpsClassRelatedEx }> = ({
  related,
}) => (
  <Card
    title={`Eval Responses · ${related.evals.count}${
      related.evals.avgRating != null
        ? ` · avg ${related.evals.avgRating}/5`
        : ''
    }`}
  >
    <div className="space-y-1.5 p-5 text-sm">
      {related.evals.recentTakeaways.length === 0 ? (
        <span className="text-gray-two">none yet</span>
      ) : (
        related.evals.recentTakeaways.map((t, i) => (
          <div key={i} className="text-xs text-gray-three">
            <span className="text-gray-two">{t.date} — </span>“{t.text}”
          </div>
        ))
      )}
    </div>
  </Card>
);

export const InstructorEvalCard: React.FC<{ related: TOpsClassRelatedEx }> = ({
  related,
}) => (
  <Card
    title={`Instructor Evaluation · ${
      (related as unknown as { instructorEvals?: unknown[] }).instructorEvals
        ?.length ?? 0
    }`}
  >
    <div className="space-y-2 p-5 text-sm">
      {(
        (
          related as unknown as {
            instructorEvals?: TInstructorEval[];
          }
        ).instructorEvals ?? []
      ).length === 0 ? (
        <span className="text-gray-two">
          No post-class form from the instructor for this class yet.
        </span>
      ) : (
        (
          (
            related as unknown as {
              instructorEvals?: TInstructorEval[];
            }
          ).instructorEvals ?? []
        ).map((e) => (
          <div key={e.id} className="text-xs text-gray-five">
            <div className="text-gray-bold">
              {e.date?.slice(0, 10) ?? '—'}
              {e.instructor ? ` · ${e.instructor}` : ''}
              {e.prepEase ? ` · prep: ${e.prepEase}` : ''}
            </div>
            {e.advice && <div>Advice for the next instructor: {e.advice}</div>}
            {e.comments && <div>{e.comments}</div>}
          </div>
        ))
      )}
    </div>
  </Card>
);

export const RelatedCasesCard: React.FC<Props> = ({
  id,
  related,
  existing,
}) => (
  <Card
    title={`Related Cases · ${related.cases.length}`}
    right={
      <Link
        className={btnGhost}
        to={`${PATH.opsCases}?new=1&classId=${id}&subject=${encodeURIComponent(
          existing?.name ?? '',
        )}`}
      >
        Open a case
      </Link>
    }
  >
    <div className="space-y-1.5 p-5 text-sm">
      {related.cases.length === 0 ? (
        <span className="text-gray-two">none</span>
      ) : (
        related.cases.map((cse) => (
          <div key={cse.id} className="truncate">
            <Link
              className="transition-colors hover:text-blue-primary"
              to={`${PATH.opsCases}?id=${cse.id}`}
            >
              <span className="text-xs text-gray-bold">
                {cse.createdDate?.slice(0, 10)} · {cse.status} ·{' '}
              </span>
              {cse.subject ?? '(no subject)'}
            </Link>
          </div>
        ))
      )}
    </div>
  </Card>
);
